import React, { useEffect, useState, useContext, useRef } from "react";
import ActionsContext from "common/ActionsContext";
import BqnContext, { usePrimaryBqn } from "common/BqnContext";
import Modal from "common/Modal.jsx";
import FileInput from "common/FileInput";
import styled from "styled-components";
import { useInstall } from "./hooks/useInstall";
import { ClusterInstallationStatus } from "./ClusterInstallationStatus";
import { checkActivationAllowed, removeClusterConfigurationText, sendForceStandalone, showRemoveClusterConfigurationWarning } from "./utils";
import ChooseInstance from "views/Administration/common/ChooseInstance";

const doesNothing = () => {};

const SourceFileInput = ({ name, onLoad = doesNothing }) => {
  const input = useRef(null);
  const handleSourceChange = (event) => {
    const [source] = event.target.files;
    onLoad(source);
  };

  useEffect(() => {
    if (input.current) {
      input.current.click();
    }
  }, [input.current]);
  return (
    <FileInput
      inputRef={input}
      text="Browse..."
      label={null}
      name={name}
      onChange={handleSourceChange}
    />
  );
};

const SourceFileInputCluster = ({
  name,
  onLoad = doesNothing,
  bqnSelected,
  setBqnSelected,
}) => {
  const {
    loginBqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const bqnPrimary = usePrimaryBqn();

  const input = useRef(null);
  const handleSourceChange = (event) => {
    const [source] = event.target.files;
    onLoad(source, bqnSelected, loginBqnId, bqnPrimary);
  };

  useEffect(() => {
    if (input.current && mode === "standalone") {
      input.current.click();
    }
  }, [input.current, mode]);

  return (
    <>
      <ChooseInstance
        action="install"
        includeAll={true}
        selected={bqnSelected}
        setSelected={setBqnSelected}
      />
      <FileInput
        inputRef={input}
        text="Browse..."
        label={null}
        name={name}
        onChange={handleSourceChange}
      />
    </>
  );
};

const TotalBar = styled.div`
  height: 1em;
  width: auto;
  border-color: var(--Text-Primary);
  border: solid 1px;
`;

const SuccessMessageDiv = styled.div`
  color: green;
`;

const ProgressBar = styled.div`
  position: relative;
  height: 1em;
  background-color: var(--Text-Primary);
`;

const ContentWrapper = styled.div`
  & div {
    display: flex;
    align-items: center;
    flex-direction: row;
  }

  i {
    font-size: 20px;
    line-height: 20px;
    margin: 0 10px;
  }
`;

const UploadProgress = ({ progress = 0, total = 1 }) => (
  <>
    <h4>Transferring software file...</h4>
    <TotalBar alt={`${progress} bytes of ${total}`}>
      <ProgressBar style={{ width: `${(progress / total) * 100}%` }} />
    </TotalBar>
  </>
);

const ReportProgress = ({
  upload = false,
  installing = false,
  completed = false,
  name,
  nameBpkg,
  loading,
  bqnSelected,
  isReloadAllowed
}) => {
  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const isReloadPackage = nameBpkg === "gui" || nameBpkg === "bqn";
  const inBqnText =
    mode === "cluster"
      ? bqnSelected === "all"
        ? " in all BQNs"
        : ` in BQN ${bqnSelected}`
      : "";
  const content = isReloadPackage ? (
    isReloadAllowed ? (
      <div>
        <ul>
          <li>
            {`Click on ACTIVATE to activate it now${inBqnText}. Activation will
              result in a traffic interruption of 10 seconds to 2 minutes,
              depending on the number of interfaces, CPU cores, and memory
              size.`}
          </li>
          <li>
            Click on CLOSE if you prefer to activate it later. You can then
            activate the installed package from this page by clicking on the{" "}
            <i className="material-icons valign-icon">cached</i> icon in the
            ACTIONS column.
          </li>
        </ul>
      </div>
    ) : (
      <div>  {removeClusterConfigurationText }</div>
    )
  ) : (
    <div>A reboot is required to activate this package.</div>
  );

  if (completed) {
    return (
      <ContentWrapper>
        <h4>{`Package ${name} installed correctly${inBqnText}.`}</h4>
        {!loading ? content : null}
      </ContentWrapper>
    );
  }
  if (installing) {
    return <h4>Installing...</h4>;
  }
  if (upload && upload !== true) {
    return <UploadProgress {...upload} />;
  }
  return null;
};

const getApply = ({
  isUploading,
  isCompleted,
  loading,
  cancel,
  doActivate,
  isReloadPackage,
  isReloadAllowed
}) => {
  if (isUploading) {
    return {
      applyLabel: "CANCEL",
      onApply: cancel,
    };
  }
  if (isCompleted) {
    if (loading) {
      return {
        applyLabel: null,
        onApply: null,
      };
    }
    if (isReloadPackage && isReloadAllowed) {
      return {
        applyLabel: "ACTIVATE",
        onApply: doActivate,
      };
    }
  }
  return {
    applyLabel: null,
    onApply: null,
  };
};

const InstallModal = ({ softwareDetail}) => {
  const [open, setOpen] = useState(false);
  const actions = useContext(ActionsContext);
  const [loading, setLoading] = useState(false);
  const [errorFooter, setErrorFooter] = useState("");

  const {
    loginBqnId,
    bqnId,
    clusterStatus: { mode },
    onlineBqns,
  } = useContext(BqnContext);

  const [bqnSelected, setBqnSelected] = useState(bqnId);

  useEffect(() => {
    setBqnSelected(bqnId);
  }, [bqnId])

  const whenDone = () => actions.send("do-load");
  const [clear, start, cancel, { error, progress, clusterInstallationStatus }] =
    useInstall({ whenDone });

  const doOpen = () => {
    clear();
    setErrorFooter("");
    setLoading(false);
    setOpen(true);
  };
  const doClose = () => {
    setOpen(false);
  };

  const doActivate = () => {
    const next = () => mode == "cluster" && bqnSelected === "all"
      ? doActivateAll()
      : doActivateOne();

    const result = checkActivationAllowed(progress.nameBpkg, progress.versionBpkg, softwareDetail, bqnSelected, loginBqnId, mode)
    if(result.reloadAllowed){
      if(result.forceStandalone){
        sendForceStandalone(bqnSelected, next)
      }else{
        next();
      }
    }else{
      return showRemoveClusterConfigurationWarning();
    }
  };

  const doActivateOne = () => {
    setErrorFooter("");
    setLoading(true);
    const isSelfReload =
      parseFloat(progress.bqnSelected) === parseFloat(loginBqnId);

    const cmd = `system software reload ${progress.nameBpkg} ${
      progress.versionBpkg
    } force${
      mode === "cluster" && !isSelfReload
        ? ` bqn-id ${progress.bqnSelected}`
        : ""
    }`;
    return ifCl
      .run(cmd)
      .then((response) => {
        if (response) {
          console.log("software reload .<%s>.error: <%s>", response);
          throw `Warning: ${response}`;
        } else if(isSelfReload){
          setTimeout(() => login.doLogout(), 5000)
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  const doActivateAll = () => {
    setErrorFooter("");
    setLoading(true);

    const cmdsOthers = onlineBqns
      .filter(({ bqnId }) => parseFloat(bqnId) !== parseFloat(loginBqnId))
      .map(({ bqnId }) => {
        return `system software reload ${progress.nameBpkg} ${progress.versionBpkg} force bqn-id ${bqnId}`;
      });

    const cmdSelf = `system software reload ${progress.nameBpkg} ${progress.versionBpkg} force`;

    return new Promise((resolve, reject) => {
      Promise.all(cmdsOthers.map((cmd) => ifCl.run(cmd)))
        .then((responses) => {
          const error = responses.find((response) => response);
          if (error) {
            return reject(error);
          } 

          ifCl
            .run(cmdSelf)
            .then((response) => {
              if (response) {
                reject(response);
              } else {
                setTimeout(() => login.doLogout(), 5000)
                resolve();
              }
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    })
      .then((response) => {
        if (response) {
          throw `Warning: ${response}`;
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  useEffect(() => actions.recv(`install-software`, () => doOpen()), []);
  if (open === false) {
    return null;
  }
  const isUploading =
    progress !== null && progress.upload && progress.upload !== true;
  const isCompleted =
    progress !== null && progress.completed && progress.completed === true;
  const isReloadPackage =
    progress !== null &&
    (progress.nameBpkg === "gui" || progress.nameBpkg === "bqn");
  const isReloadAllowed =
    isReloadPackage &&
    softwareDetail &&
    checkActivationAllowed(
      progress.nameBpkg,
      progress.versionBpkg,
      softwareDetail,
      bqnSelected,
      loginBqnId,
      mode,
    ).reloadAllowed;
  
  const { applyLabel, onApply } = getApply({
    isUploading,
    isCompleted,
    loading,
    cancel,
    doActivate,
    isReloadPackage,
    isReloadAllowed
  });

  function showError(){
    if(!error){
      return null;
    }

    const isAlreadyInstalledError = error.match(/%ERR: Package "(.*)" already installed/);

    if(isAlreadyInstalledError){
      return <SuccessMessageDiv className="margin-t-15 text-align-left pre-wrap">
          Package {isAlreadyInstalledError[1]} already installed.
        </SuccessMessageDiv>
    }else{
      return (
        <div className="margin-t-15 color-red text-align-left pre-wrap">
          {String(error)}
        </div>
      );
    }
  }
  return (
    <Modal
      title="Install .bpkg file"
      className="modal-software-install"
      superIcon="add_circle"
      content={() => (
        <>
          {progress === null ? (
            mode === "standalone" ? (
              <SourceFileInput name="source" onLoad={start} />
            ) : (
              <SourceFileInputCluster
                name="source"
                onLoad={start}
                bqnSelected={bqnSelected}
                setBqnSelected={setBqnSelected}
              />
            )
          ) : (
            <>
              <ReportProgress {...progress} loading={loading} isReloadAllowed={isReloadAllowed}/>
              {loading ? (
                <div>
                  <b>Activating</b>: Wait a few seconds and refresh the browser.
                </div>
              ) : null}
            </>
          )}

          <ClusterInstallationStatus data={clusterInstallationStatus} />

          {showError()}
        </>
      )}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
      applyLabel={applyLabel}
      onApply={onApply}
      closeLabel={isUploading ? null : "CLOSE"}
      onClose={isUploading ? null : doClose}
    />
  );
};

export default InstallModal;
